import { createEnv } from '@t3-oss/env-nextjs'
import { z } from 'zod'

import { PODO_BACKEND_COMPAT_RELEASE } from '../../apis/podo-backend-release.ts'
import { commonClientSchema, commonServerSchema, commonSharedSchema } from './schemas.ts'

// Web + Server 통합 서버 환경 변수
const webServerSchema = {
  ...commonServerSchema,

  // JWT 인증 (서버에서 사용)
  JWT_SECRET: z.string().min(1),

  // GKE workload identity delegation for verified legacy BFF session migration.
  PODO_SYSTEM_TOKEN_SERVICE: z.string().min(1).optional(),
  PODO_SYSTEM_TOKEN_AUDIENCE: z.string().min(1).optional(),
  PODO_BACKEND_RELEASE: z.literal(PODO_BACKEND_COMPAT_RELEASE).optional(),

  // GCP Pub/Sub (서버에서 사용)
  GCP_PROJECT_ID: z.string().min(1),
  PUBSUB_TOPIC_NAME: z.string().min(1),

  // 결제 관련 (IamPort V1) - 서버와 웹에서 모두 사용
  IMP_CUSTOMER_KEY: z.string(),
  IMP_KEY: z.string(),
  IMP_SECRET: z.string(),
  IMP_TOSS_PAYMENT_CHANNEL_KEY: z.string(),
  IMP_KAKAO_PAY_CHANNEL_KEY: z.string(),
  IMP_NAVER_PAY_CHANNEL_KEY: z.string(),

  // 결제 관련 (PortOne V2)
  PORTONE_API_SECRET: z.string(),

  // 무료체험 웹훅 서버 간 서명. 발급 요청 시 UTF-8 32바이트 이상을 검증한다.
  PODO_TRIAL_FREE_WEBHOOK_SECRET: z.string().optional(),

  // Apple OAuth (서버에서 사용)
  APPLE_KEY_ID: z.string(),
  APPLE_TEAM_ID: z.string(),
  APPLE_OAUTH_KEY: z.string(),

  // Web 앱 전용
  REVALIDATE_KEY: z.string(),

  // Google Analytics
  GOOGLE_ANALYTICS_ID: z.string(),
  GOOGLE_TAG_MANAGER_ID: z.string(),

  // 추가 URL들
  PODO_APP_TEMP_URL: z.string().url(),

  // 추가 외부 서비스
  KAKAO_JS_SDK_KEY: z.string(),

  // GrowthBook
  GROWTHBOOK_API_HOST: z.string().url(),
  GROWTHBOOK_CLIENT_KEY: z.string().min(1),
  // Notion Integration
  NOTION_API_KEY: z.string().min(1),

  // Onetalk 체험수업 handoff (서버 간 호출)
  ONETALK_INTEGRATION_API_URL: z.string().url().optional(),
  ONETALK_INTEGRATION_OIDC_AUDIENCE: z.string().min(1).optional(),
  ONETALK_INTEGRATION_OIDC_SERVICE_ACCOUNT: z.string().email().optional(),
  /**
   * 코스 카드를 받아 올 podo-curriculum 공유 런타임 버전. 비워 두면 `latest` 라,
   * 카드를 다시 구워 publish 하면 여기 배포 없이 반영된다. 잘못 나간 카드를 급히
   * 되돌려야 할 때만 태그(예: v1.18.0)를 넣어 고정한다.
   */
  SHARED_RUNTIME_VERSION: z.string().min(1).optional(),
} as const

// Web 앱 전용 클라이언트 환경 변수
const webClientSchema = {
  ...commonClientSchema,

  // 추가 URL들
  NEXT_PUBLIC_PODO_APP_TEMP_URL: z.string().url(),
  NEXT_PUBLIC_PODO_WEB_URL: z.string().url(),
  NEXT_PUBLIC_PODO_ADMIN_URL: z.string().url(),

  // 결제 관련 (V1)
  NEXT_PUBLIC_IMP_CUSTOMER_KEY: z.string(),
  NEXT_PUBLIC_IMP_TOSS_PAYMENT_CHANNEL_KEY: z.string(),
  NEXT_PUBLIC_IMP_KAKAO_PAY_CHANNEL_KEY: z.string(),
  NEXT_PUBLIC_IMP_KAKAO_PAY_ONE_TIME_CHANNEL_KEY: z.string().min(1).optional(),
  NEXT_PUBLIC_IMP_NAVER_PAY_CHANNEL_KEY: z.string(),

  // 결제 관련 (PortOne V2)
  NEXT_PUBLIC_PORTONE_STORE_ID: z.string(),

  // 외부 서비스
  NEXT_PUBLIC_KAKAO_CLIENT_ID: z.string(),
  NEXT_PUBLIC_KAKAO_JS_SDK_KEY: z.string(),

  // Google Analytics
  NEXT_PUBLIC_GOOGLE_ANALYTICS_ID: z.string(),
  NEXT_PUBLIC_GOOGLE_TAG_MANAGER_ID: z.string(),

  NEXT_PUBLIC_CDN_BASE_URL: z.string().url().optional(),

  // Onetalk 상담 위젯 (클라이언트 공개)
  NEXT_PUBLIC_ONETALK_BASE_URL: z.string().url().optional(),
  NEXT_PUBLIC_ONETALK_CHANNEL_API_KEY: z.string().min(1).optional(),
  NEXT_PUBLIC_ONETALK_SUPPORT_URL: z.string().url().optional(),
} as const

// env 객체 - @t3-oss/env-nextjs를 사용한 타입 안전 환경 변수 검증
export const env = createEnv({
  skipValidation: process.env.CI === 'true' || process.env.NODE_ENV === 'test',

  shared: commonSharedSchema,
  server: webServerSchema,
  client: webClientSchema,

  experimental__runtimeEnv: {
    NODE_ENV: process.env.NODE_ENV,

    // Google Analytics
    NEXT_PUBLIC_GOOGLE_ANALYTICS_ID: process.env.NEXT_PUBLIC_GOOGLE_ANALYTICS_ID,
    NEXT_PUBLIC_GOOGLE_TAG_MANAGER_ID: process.env.NEXT_PUBLIC_GOOGLE_TAG_MANAGER_ID,

    NEXT_PUBLIC_APP_ENV: process.env.NEXT_PUBLIC_APP_ENV,
    NEXT_PUBLIC_API_URL: process.env.NEXT_PUBLIC_API_URL,
    NEXT_PUBLIC_PODO_API_URL: process.env.NEXT_PUBLIC_PODO_API_URL,
    NEXT_PUBLIC_PODO_APP_TEMP_URL: process.env.NEXT_PUBLIC_PODO_APP_TEMP_URL,
    NEXT_PUBLIC_PODO_WEB_URL: process.env.NEXT_PUBLIC_PODO_WEB_URL,
    NEXT_PUBLIC_PODO_ADMIN_URL: process.env.NEXT_PUBLIC_PODO_ADMIN_URL,
    NEXT_PUBLIC_KAKAO_CLIENT_ID: process.env.NEXT_PUBLIC_KAKAO_CLIENT_ID,
    NEXT_PUBLIC_KAKAO_JS_SDK_KEY: process.env.NEXT_PUBLIC_KAKAO_JS_SDK_KEY,
    NEXT_PUBLIC_IMP_CUSTOMER_KEY: process.env.NEXT_PUBLIC_IMP_CUSTOMER_KEY,
    NEXT_PUBLIC_IMP_TOSS_PAYMENT_CHANNEL_KEY: process.env.NEXT_PUBLIC_IMP_TOSS_PAYMENT_CHANNEL_KEY,
    NEXT_PUBLIC_IMP_KAKAO_PAY_CHANNEL_KEY: process.env.NEXT_PUBLIC_IMP_KAKAO_PAY_CHANNEL_KEY,
    // 단건 테스트 채널은 stage/qa에서만 기본 사용한다. 운영 키는 빌드 전에 별도로 설정해야 한다.
    NEXT_PUBLIC_IMP_KAKAO_PAY_ONE_TIME_CHANNEL_KEY:
      process.env.NEXT_PUBLIC_IMP_KAKAO_PAY_ONE_TIME_CHANNEL_KEY?.trim() ||
      (process.env.NEXT_PUBLIC_APP_ENV === 'staging' || process.env.NEXT_PUBLIC_APP_ENV === 'qa'
        ? 'channel-key-8e696940-25b3-480a-8db9-33896e41c6b6'
        : undefined),
    NEXT_PUBLIC_IMP_NAVER_PAY_CHANNEL_KEY: process.env.NEXT_PUBLIC_IMP_NAVER_PAY_CHANNEL_KEY,
    NEXT_PUBLIC_PORTONE_STORE_ID: process.env.NEXT_PUBLIC_PORTONE_STORE_ID,
    NEXT_PUBLIC_CDN_BASE_URL: process.env.NEXT_PUBLIC_CDN_BASE_URL,

    NEXT_PUBLIC_ONETALK_BASE_URL: process.env.NEXT_PUBLIC_ONETALK_BASE_URL,
    NEXT_PUBLIC_ONETALK_CHANNEL_API_KEY: process.env.NEXT_PUBLIC_ONETALK_CHANNEL_API_KEY,
    NEXT_PUBLIC_ONETALK_SUPPORT_URL: process.env.NEXT_PUBLIC_ONETALK_SUPPORT_URL,
  },
})

export type Env = typeof env

// 기존 호환성을 위한 상수들
export const featureFlagEnvSchemaMap = {
  local: 'development',
  development: 'development',
  staging: 'staging',
  production: 'production',
  temp: 'temp',
  qa: 'qa',
} as const

export * from './schemas.ts'
